import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, filter, map, mergeMap, Observable, of, Subject, switchMap } from 'rxjs';
import { catchError, finalize, take, takeUntil, tap } from 'rxjs/operators';

import { ApiErrorResponse, SiteState } from 'src/app/shared/models';

import { DateService } from '../../shared/services/date.service';
import {
  AvailableBemCropVariety,
  ChangeCropVarietyRequest,
  BfpState,
  ChangePlantDateRequest,
  ChangePlantDateResponse,
  CropFieldBemDto,
  CropFieldNutrientCalculations,
  GetCropFieldBemResponse,
  GetCropFieldTrendLineResponse,
  GetNutrientAdviceBemResponse,
  GetSoilCropAnalysesResponse,
} from '../shared/models/models';
import { BemApiService } from '../shared/services/bem-api.service';
import { loadBFPData } from '../store/bfp.actions';
import { selectCropFieldNutrientCalculation } from '../store/bfp.feature';
import { CropFieldInfoComponent } from './components/cropfield-info/cropfield-info.component';
import { CropSoilAnalysisBlockComponent } from './components/crop-soil-analysis-block/crop-soil-analysis-block.component';
import { NutrientAdviceComponent } from './components/nutrient-advice/nutrient-advice.component';
import { TrendlineGraphsComponent } from './components/trendline-graphs/trendline-graphs.component';
import { FooterComponent } from './components/footer/footer.component';
import { AsyncPipe } from '@angular/common';
import { selectCropYear } from '../../store/selectors';

@Component({
  selector: 'app-bem-advice',
  templateUrl: './bem-advice.component.html',
  styleUrls: ['./bem-advice.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CropFieldInfoComponent,
    CropSoilAnalysisBlockComponent,
    NutrientAdviceComponent,
    TrendlineGraphsComponent,
    FooterComponent,
    AsyncPipe,
  ],
})
export class BemAdviceComponent implements OnInit, OnDestroy {
  private readonly bemApiService = inject(BemApiService);
  private readonly appStore = inject<Store<SiteState>>(Store);
  private readonly bemStore = inject<Store<BfpState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dateService = inject(DateService);

  readonly destroy$ = new Subject<void>();
  private readonly fieldChanged$ = new Subject<void>();
  readonly availableVarieties = signal<AvailableBemCropVariety[]>([]);
  readonly varietiesLoadFailed = signal(false);
  readonly isSaving = signal(false);

  calculations$: Observable<CropFieldNutrientCalculations[]> = this.bemStore.select(selectCropFieldNutrientCalculation);

  readonly onParamsChange$ = this.route.params.pipe(
    takeUntil(this.destroy$),
  );

  bemData$ = new BehaviorSubject<GetCropFieldBemResponse | null>(null);
  isSeedpotato$: Observable<boolean> = this.bemData$.pipe(
    map((response) => response != null && 'cropField' in response && response.productionPurposeCategory?.name === 'PA'),
  );
  nutrientAdviceData$: Subject<GetNutrientAdviceBemResponse | null> = new BehaviorSubject<GetNutrientAdviceBemResponse | null>(null);
  trendLineData$: Subject<GetCropFieldTrendLineResponse | null> = new BehaviorSubject<GetCropFieldTrendLineResponse | null>(null);
  cropSoilAnalysisData$: Subject<GetSoilCropAnalysesResponse | null> = new BehaviorSubject<GetSoilCropAnalysesResponse | null>(null);

  ngOnInit(): void {
    this.retrieveAllCropFieldNutrientCalculations();

    this.onParamsChange$.pipe(
      takeUntil(this.destroy$),
      filter((params) => !!params.cropfieldId),
      tap(() => {
        this.fieldChanged$.next();
        this.bemData$.next(null);
        this.availableVarieties.set([]);
        this.varietiesLoadFailed.set(false);
      }),
      switchMap((params) => this.bemApiService.getCropFieldBem(params.cropfieldId)),
      tap((result) => {
        this.bemData$.next(result);
        if (isCropFieldBemResponse(result) && !result.cropField.isPlantDateConfirmed) {
          this.loadAvailableVarieties(result.cropField.id);
        }
      }),
    ).subscribe();

    this.onParamsChange$.pipe(
      takeUntil(this.destroy$),
      filter((params) => !!params.cropfieldId),
      tap(() => this.nutrientAdviceData$.next(null)),
      switchMap((params) => this.bemApiService.getNutrientAdviceBem(params.cropfieldId)),
      tap((result) => this.nutrientAdviceData$.next(result)),
    ).subscribe();

    this.onParamsChange$.pipe(
      takeUntil(this.destroy$),
      filter((params) => !!params.cropfieldId),
      tap(() => this.trendLineData$.next(null)),
      switchMap((params) => this.bemApiService.getCropFieldTrendLine(params.cropfieldId)),
      tap((result) => this.trendLineData$.next(result)),
    ).subscribe();

    this.onParamsChange$.pipe(
      takeUntil(this.destroy$),
      filter((params) => !!params.cropfieldId),
      tap(() => this.cropSoilAnalysisData$.next(null)),
      switchMap((params) => this.bemApiService.getSoilCropAnalyses(params.cropfieldId)),
      tap((result) => this.cropSoilAnalysisData$.next(result)),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.fieldChanged$.complete();
  }

  async onCropFieldChange(cropFieldId: number): Promise<void> {
    await this.router.navigate(['../', cropFieldId], { relativeTo: this.route });
  }

  onPlantDateChange(request: ChangePlantDateRequest): void {
    if (this.isSaving()) return;
    this.isSaving.set(true);
    this.bemApiService
      .changePlantDate(request)
      .pipe(
        catchError((error: HttpErrorResponse) => of(error.error)),
        mergeMap((response: ChangePlantDateResponse) => {
          if ('statusCode' in response) {
            return of({ response, bemData: null as GetCropFieldBemResponse | null });
          }

          return this.bemApiService.getCropFieldBem(request.cropFieldId).pipe(
            map((bemData) => ({ response, bemData })),
          );
        }),
        takeUntil(this.fieldChanged$),
        takeUntil(this.destroy$),
        finalize(() => this.isSaving.set(false)),
      )
      .subscribe(({ response, bemData }) => {
        if ('statusCode' in response) {
          if (response.validationMessageCodes) {
            response.validationMessageCodes.forEach((code) => {
              this.appStore.dispatch(NotificationActions.errorSnackbar({
                message: this.locale.translate(`BFP.Validation.Backend.${code}`),
              }));
            });
          } else {
            this.appStore.dispatch(NotificationActions.errorSnackbar({
              message: response.message,
            }));
          }

          this.restoreCurrentFieldData();
        } else {
          if (bemData) {
            this.bemData$.next(bemData);
          }
          this.appStore.dispatch(NotificationActions.infoSnackbar({
            message: this.locale.translate('BFP.Notifications.PlantDateUpdated', {
              plantDate: response.plantDate?.toLocaleDateString(this.locale.getLocale()) ?? 'Failed to parse plant date',
            }),
          }));
        }
      });
  }

  loadAvailableVarieties(cropFieldId: number): void {
    this.availableVarieties.set([]);
    this.varietiesLoadFailed.set(false);
    this.bemApiService.getAvailableCropVarieties(cropFieldId).pipe(
      takeUntil(this.fieldChanged$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (varieties) => this.availableVarieties.set(varieties),
      error: () => this.varietiesLoadFailed.set(true),
    });
  }

  onCropVarietyChange(request: ChangeCropVarietyRequest): void {
    if (this.isSaving()) return;
    this.isSaving.set(true);
    this.bemApiService.changeCropVariety(request).pipe(
      switchMap(() => this.bemApiService.getCropFieldBem(request.cropFieldId)),
      takeUntil(this.fieldChanged$),
      takeUntil(this.destroy$),
      finalize(() => this.isSaving.set(false)),
    ).subscribe({
      next: (bemData) => {
        if (!isCropFieldBemResponse(bemData)) {
          this.restoreCurrentFieldData();
          this.appStore.dispatch(NotificationActions.errorSnackbar({
            message: this.locale.translate('BFP.Notifications.CropVarietyReloadFailed'),
          }));
          return;
        }
        this.bemData$.next(bemData);
        this.retrieveAllCropFieldNutrientCalculations();
        this.appStore.dispatch(NotificationActions.infoSnackbar({
          message: this.locale.translate('BFP.Notifications.CropVarietyUpdated'),
        }));
      },
      error: (error: HttpErrorResponse) => {
        this.restoreCurrentFieldData();
        const response: ApiErrorResponse | null = error.error;
        const codes = response?.validationMessageCodes;
        (codes?.length ? codes : ['CropVarietyUpdateFailed']).forEach((code) => {
          this.appStore.dispatch(NotificationActions.errorSnackbar({
            message: this.locale.translate(`BFP.Validation.Backend.${code}`),
          }));
        });
      },
    });
  }

  private retrieveAllCropFieldNutrientCalculations(): void {
    this.appStore.pipe(select(selectCropYear), take(1)).subscribe((cropYear) => {
      this.bemStore.dispatch(loadBFPData({ year: cropYear }));
    });
  }

  private restoreCurrentFieldData(): void {
    const currentBemData = this.bemData$.getValue();
    if (!isCropFieldBemResponse(currentBemData)) {
      return;
    }

    this.bemData$.next({
      ...currentBemData,
      cropField: {
        ...currentBemData.cropField,
      },
    });
  }
}

function isCropFieldBemResponse(response: GetCropFieldBemResponse | null | undefined): response is CropFieldBemDto {
  return !!response && 'cropField' in response;
}
